import { z } from 'zod';

/*
 * The rules a link's address meets before any request is sent (P13, P14, I3).
 *
 * The same rules as the backend's request schema (`app/schemas/link.py`,
 * backend B5, D5), in its words where it has them, plus one of the portal's
 * own: no space inside the address, which in a pasted URL is a typo. The
 * server stays the authority; its 422 is shown on the field as well.
 */

const MAX_LENGTH = 2048;

export const LINK_URL_MESSAGES = {
  notSecure: 'Enter a full secure address beginning with https://',
  space: 'The address cannot contain spaces.',
  unparsable: 'Enter a valid web address.',
  userInfo: 'The address cannot carry a user name or password.',
  port: 'The address cannot name a port other than 443.',
  ipLiteral: 'Enter the address of a public website, not an IP address.',
  notPublic: 'Enter the address of a public website.',
  tooLong: 'The address can be at most 2,048 characters.',
} as const;

const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;

/** The first rule a trimmed address breaks, in design.md's order, or null. */
function firstRefusal(address: string): string | null {
  if (!/^https:\/\//i.test(address)) return LINK_URL_MESSAGES.notSecure;
  if (/\s/.test(address)) return LINK_URL_MESSAGES.space;
  let parsed: URL;
  try {
    parsed = new URL(address);
  } catch {
    return LINK_URL_MESSAGES.unparsable;
  }
  if (parsed.username || parsed.password) return LINK_URL_MESSAGES.userInfo;
  // `URL` drops the default port, so an explicit :443 reads as empty here.
  if (parsed.port !== '' && parsed.port !== '443') return LINK_URL_MESSAGES.port;
  const host = parsed.hostname;
  // `URL` writes a numeric host as dotted IPv4 and keeps IPv6 in brackets.
  if (IPV4.test(host) || host.startsWith('[')) return LINK_URL_MESSAGES.ipLiteral;
  if (host === 'localhost' || !host.includes('.')) return LINK_URL_MESSAGES.notPublic;
  if (address.length > MAX_LENGTH) return LINK_URL_MESSAGES.tooLong;
  return null;
}

/** A link's address: trimmed, then every rule; the value is kept as typed. */
export const linkUrlSchema = z
  .string()
  .trim()
  .superRefine((address, ctx) => {
    const message = firstRefusal(address);
    if (message) ctx.addIssue({ code: 'custom', message });
  });

/**
 * The sub-page's form. An empty field is a valid form value (P14): Save Draft
 * and Publish Changes stay disabled for it through `canSave` and `canPublish`.
 * The mode is only ever EXTERNAL (P9, I5).
 */
export const linkFormSchema = z.object({
  url: z
    .string()
    .trim()
    .superRefine((address, ctx) => {
      if (address === '') return;
      const message = firstRefusal(address);
      if (message) ctx.addIssue({ code: 'custom', message });
    }),
  mode: z.literal('EXTERNAL').optional(),
});

export type LinkForm = z.infer<typeof linkFormSchema>;

/** Whether Save Draft may send this value: non-empty and valid (P14). */
export function canSave(value: string): boolean {
  const address = value.trim();
  return address !== '' && firstRefusal(address) === null;
}

/** Whether Publish Changes may send this value: a valid address (P14). */
export function canPublish(value: string): boolean {
  return canSave(value);
}
